import { CHAT_TIMING_PARALLEL_MIN_OVERLAP_MS } from "@/constants";
import {
  derivePhase,
  formatElapsed,
  TURN_PHASES,
  type TurnPhase,
} from "@/features/chat/lib/turnPhase";
import type { PipelineVerboseStep } from "@/features/chat/lib/turnStream";

/**
 * Where a finished turn's time went, read off its steps.
 *
 * The backend measures the steps that matter -- each stage's completion step carries
 * `duration_ms` (intent classified, concept resolved, planning decision, SQL generated and run,
 * synthesis, critique, validation, final response) and `turn_completed` carries the whole turn.
 * Grouping those by reader-facing phase gives the breakdown; whatever the measured steps do not
 * cover (queueing, the follow-up resolver, unmeasured tool calls) is reported as "other" rather
 * than hidden, so the bars always add up to the total shown.
 *
 * A stage is filed under the phase its duration measured. That is the live label's phase for most
 * of them, but not all: the SQL result is the live cue that the writing has begun, while its
 * duration is the warehouse running the query.
 *
 * Steps also carry `elapsed_ms`, when they ended on the turn's clock, which places each one on a
 * timeline. Some steps run side by side -- the writer, evidence gate and evidence review of the
 * answer review; the chart designer beside the writing; the core response beside the research.
 * On the timeline a phase's time is the stretch its stages covered with overlaps counted once,
 * and every step knows which others ran at the same time. A gap between two stages of one phase
 * that no other phase's stage ran in is that phase's time too: the SQL agent re-validating its
 * query between "SQL ready" and the warehouse run is part of running the query, not "other".
 * Turns recorded before steps carried `elapsed_ms` keep the plain sum.
 *
 * Inside a stage, each model call and each agent tool call is timed too. Those are sub-steps:
 * already part of their stage's own duration, so they are listed under the phase and never added
 * to it. They are listed only with `includeCalls` -- for holders of `chat-call-timing-view` (an
 * unreleased permission, so a Super Administrator for now): the drill-down is a debugging aid,
 * not reader copy, and the backend sends the per-call timing rows to nobody else.
 */

export type SubStepKind = "llm" | "tool";

/** A stretch of the turn's timeline, in ms from the turn's start. */
export type TimeSpan = {
  startMs: number;
  endMs: number;
};

export type SubStepTiming = {
  stepIndex: number;
  kind: SubStepKind;
  /** The step's own title: "LLM call — intent", "SQL Agent — execute_sql result". */
  label: string;
  ms: number;
  /** Where the call sat on the timeline; null on turns recorded before steps carried it. */
  span: TimeSpan | null;
  /** The other calls that were running at the same time; empty when it ran alone. */
  parallelWith: string[];
};

export type PhaseTiming = {
  phase: TurnPhase;
  /**
   * The phase's time: its stretches of the timeline, or its stages' durations added up on a turn
   * without one.
   */
  ms: number;
  /**
   * Its stages' durations added up -- more than `ms` when some ran side by side, less when a gap
   * between two of them was joined into the phase.
   */
  workMs: number;
  /**
   * The stretches of the timeline the phase's stages covered, merged, and joined across a gap
   * between two of them that no other phase's stage ran in; empty without a timeline.
   */
  spans: TimeSpan[];
  /** Its stages that ran alongside another stage; empty when they ran one after another. */
  parallelStages: string[];
  /** Model and tool calls made during this phase, in the order they happened. */
  subSteps: SubStepTiming[];
};

export type TurnTiming = {
  /** The whole turn, from `turn_completed`; null when the turn never finished. */
  totalMs: number | null;
  /** Measured time per phase, in pipeline order; phases with neither time nor calls omitted. */
  byPhase: PhaseTiming[];
  /** Total minus the phases' time; never negative. */
  otherMs: number;
  /** The stretches of the timeline no phase covered. */
  otherSpans: TimeSpan[];
  /** Model calls that ran outside every phase, so their time is part of `otherMs`. */
  otherSubSteps: SubStepTiming[];
  /** Every measured step knows where it sat in time, so bars can be placed on a timeline. */
  hasTimeline: boolean;
  /** The listed steps' durations added up, as if each had waited for the one before it. */
  workMs: number;
  /** The wall-clock time those steps covered together. */
  coveredMs: number;
  /** `workMs - coveredMs`: the time running steps side by side saved. */
  parallelSavedMs: number;
};

const TURN_COMPLETED_EVENT = "turn_completed";
const LLM_CALL_EVENT = "llm_call";
/** A tool call timed outside an agent's own loop -- the warehouse, semantic retrieval, web search. */
const TOOL_TIMING_EVENT = "tool_timing";
/** A resumed turn starts a fresh clock, so steps before the pause cannot share its timeline. */
const CLARIFICATION_ANSWERED_EVENT = "clarification_answered";
const TOOL_EVENTS = new Set(["tool_call", "tool_result"]);
/** Agents that run tools in a loop; each tool result is timed inside the stage's duration. */
const AGENT_TOOL_TITLE_PREFIXES = ["Context Planning", "SQL Agent"];
/** Runs before the pipeline graph, so no stage measures it: its time is "other". */
const PRE_STAGE_LLM_CALL_TITLE = "LLM call — follow_up_resolver";
/**
 * Stages whose duration belongs to an earlier phase than the one their event starts. The SQL
 * result moves the live label on to the writing, but what it measured is the query's run.
 */
const MEASURED_PHASE_BY_EVENT = new Map<string, TurnPhase>([
  ["sql_execution_result", "querying"],
]);

type StageTiming = {
  phase: TurnPhase;
  label: string;
  ms: number;
  span: TimeSpan | null;
};

function measuredPhase(step: PipelineVerboseStep): TurnPhase | null {
  return MEASURED_PHASE_BY_EVENT.get(step.eventType) ?? derivePhase(null, step);
}

function isAgentToolStep(step: PipelineVerboseStep): boolean {
  return (
    TOOL_EVENTS.has(step.eventType) &&
    AGENT_TOOL_TITLE_PREFIXES.some((prefix) => step.stepTitle.startsWith(prefix))
  );
}

function spanOf(step: PipelineVerboseStep, durationMs: number): TimeSpan | null {
  if (typeof step.elapsedMs !== "number") {
    return null;
  }
  return { startMs: Math.max(0, step.elapsedMs - durationMs), endMs: step.elapsedMs };
}

function toSubStep(step: PipelineVerboseStep, kind: SubStepKind, ms: number): SubStepTiming {
  return {
    stepIndex: step.stepIndex,
    kind,
    label: step.stepTitle,
    ms,
    span: spanOf(step, ms),
    parallelWith: [],
  };
}

function overlapMs(first: TimeSpan, second: TimeSpan): number {
  return Math.max(
    0,
    Math.min(first.endMs, second.endMs) - Math.max(first.startMs, second.startMs),
  );
}

function ranAlongside(first: TimeSpan | null, second: TimeSpan | null): boolean {
  return (
    first !== null &&
    second !== null &&
    overlapMs(first, second) > CHAT_TIMING_PARALLEL_MIN_OVERLAP_MS
  );
}

/** Sorted, with overlapping or touching stretches joined into one. */
export function mergeSpans(spans: TimeSpan[]): TimeSpan[] {
  const sorted = [...spans]
    .filter((span) => span.endMs > span.startMs)
    .sort((first, second) => first.startMs - second.startMs);
  const merged: TimeSpan[] = [];
  for (const span of sorted) {
    const last = merged.at(-1);
    if (last && span.startMs <= last.endMs) {
      last.endMs = Math.max(last.endMs, span.endMs);
    } else {
      merged.push({ ...span });
    }
  }
  return merged;
}

function coveredLength(spans: TimeSpan[]): number {
  return mergeSpans(spans).reduce((sum, span) => sum + span.endMs - span.startMs, 0);
}

/** The stretches of `[0, totalMs]` that none of `spans` covers. */
function gapsWithin(totalMs: number, spans: TimeSpan[]): TimeSpan[] {
  const gaps: TimeSpan[] = [];
  let cursor = 0;
  for (const span of mergeSpans(spans)) {
    if (span.startMs > cursor) {
      gaps.push({ startMs: cursor, endMs: Math.min(span.startMs, totalMs) });
    }
    cursor = Math.max(cursor, span.endMs);
  }
  if (cursor < totalMs) {
    gaps.push({ startMs: cursor, endMs: totalMs });
  }
  return gaps.filter((gap) => gap.endMs > gap.startMs);
}

function markParallel(subSteps: SubStepTiming[]): void {
  for (const subStep of subSteps) {
    subStep.parallelWith = subSteps
      .filter((other) => other !== subStep && ranAlongside(subStep.span, other.span))
      .map((other) => other.label);
  }
}

/**
 * An LLM call recorded for its timing alone, without the raw request and response. It feeds the
 * time breakdown's per-call rows and has nothing to show in a list of steps.
 */
export function isCallTimingStep(step: PipelineVerboseStep): boolean {
  if (step.eventType === TOOL_TIMING_EVENT) {
    return true;
  }
  return (
    step.eventType === LLM_CALL_EVENT &&
    !step.rawResponse &&
    !(step.messages && step.messages.length > 0)
  );
}

/** Chronological on a timeline, else in log order. */
function byOrder(first: SubStepTiming, second: SubStepTiming): number {
  if (first.span && second.span && first.span.startMs !== second.span.startMs) {
    return first.span.startMs - second.span.startMs;
  }
  return first.stepIndex - second.stepIndex;
}

function stageSpans(stages: StageTiming[]): TimeSpan[] {
  return stages.flatMap((stage) => (stage.span ? [stage.span] : []));
}

/**
 * Each phase's stretches of the timeline: its stages' spans merged, joined across a gap between
 * two of them when no other phase's stage ran in it.
 */
function phaseSpansOf(stages: StageTiming[]): Map<TurnPhase, TimeSpan[]> {
  const spansByPhase = new Map<TurnPhase, TimeSpan[]>();
  for (const phase of TURN_PHASES) {
    const otherPhaseSpans = stageSpans(stages.filter((stage) => stage.phase !== phase));
    const joined: TimeSpan[] = [];
    for (const span of mergeSpans(stageSpans(stages.filter((stage) => stage.phase === phase)))) {
      const last = joined.at(-1);
      const gap = last ? { startMs: last.endMs, endMs: span.startMs } : null;
      if (last && gap && !otherPhaseSpans.some((other) => overlapMs(other, gap) > 0)) {
        last.endMs = span.endMs;
      } else {
        joined.push(span);
      }
    }
    if (joined.length > 0) {
      spansByPhase.set(phase, joined);
    }
  }
  return spansByPhase;
}

/** The phase whose stretches `span` overlapped most; null when it overlapped none. */
function phaseOverlapping(
  spansByPhase: Map<TurnPhase, TimeSpan[]>,
  span: TimeSpan | null,
): TurnPhase | null {
  let best: TurnPhase | null = null;
  let bestOverlap = 0;
  for (const [phase, spans] of spansByPhase) {
    const overlap = span
      ? spans.reduce((sum, phaseSpan) => sum + overlapMs(phaseSpan, span), 0)
      : 0;
    if (overlap > bestOverlap) {
      best = phase;
      bestOverlap = overlap;
    }
  }
  return best;
}

export type TurnTimingOptions = {
  /** List each model and tool call under its phase: the `chat-call-timing-view` view. */
  includeCalls?: boolean;
};

export function summarizeTurnTiming(
  verboseLog: PipelineVerboseStep[],
  { includeCalls = false }: TurnTimingOptions = {},
): TurnTiming {
  const completed = verboseLog.find((step) => step.eventType === TURN_COMPLETED_EVENT);
  const totalMs =
    completed && typeof completed.durationMs === "number" ? completed.durationMs : null;
  const measured = verboseLog.filter(
    (step) => step.eventType !== TURN_COMPLETED_EVENT && typeof step.durationMs === "number",
  );
  const hasTimeline =
    totalMs !== null &&
    measured.length > 0 &&
    measured.every((step) => typeof step.elapsedMs === "number") &&
    !verboseLog.some((step) => step.eventType === CLARIFICATION_ANSWERED_EVENT);

  const stages: StageTiming[] = [];
  const subStepsByPhase = new Map<TurnPhase, SubStepTiming[]>();
  const otherSubSteps: SubStepTiming[] = [];
  // An LLM call's title does not name its stage, but every stage emits its timed step after its
  // calls return, so a call belongs to the next stage that reports a duration.
  let awaitingStage: SubStepTiming[] = [];

  const addSubSteps = (phase: TurnPhase, subSteps: SubStepTiming[]) => {
    if (includeCalls && subSteps.length > 0) {
      subStepsByPhase.set(phase, [...(subStepsByPhase.get(phase) ?? []), ...subSteps]);
    }
  };

  for (const step of measured) {
    const durationMs = step.durationMs ?? 0;
    if (step.eventType === LLM_CALL_EVENT || step.eventType === TOOL_TIMING_EVENT) {
      const call = toSubStep(step, step.eventType === LLM_CALL_EVENT ? "llm" : "tool", durationMs);
      if (step.stepTitle === PRE_STAGE_LLM_CALL_TITLE) {
        otherSubSteps.push(call);
      } else {
        awaitingStage.push(call);
      }
      continue;
    }
    const phase = measuredPhase(step);
    if (phase === null) {
      continue;
    }
    if (isAgentToolStep(step)) {
      addSubSteps(phase, [toSubStep(step, "tool", durationMs)]);
      continue;
    }
    stages.push({ phase, label: step.stepTitle, ms: durationMs, span: spanOf(step, durationMs) });
    addSubSteps(phase, awaitingStage);
    awaitingStage = [];
  }
  otherSubSteps.push(...awaitingStage);

  const spansByPhase = hasTimeline ? phaseSpansOf(stages) : new Map<TurnPhase, TimeSpan[]>();

  // On a timeline a call belongs to the phase it overlapped most. Right even for a call reported
  // out of order: a background call replayed later (the chart designer), or a tool call timed and
  // saved when the turn ended. Turns without a timeline keep the log-order rule above.
  if (hasTimeline && includeCalls) {
    const placed = [...[...subStepsByPhase.values()].flat(), ...otherSubSteps];
    subStepsByPhase.clear();
    otherSubSteps.length = 0;
    for (const subStep of placed) {
      const phase = phaseOverlapping(spansByPhase, subStep.span);
      if (phase === null) {
        otherSubSteps.push(subStep);
      } else {
        subStepsByPhase.set(phase, [...(subStepsByPhase.get(phase) ?? []), subStep]);
      }
    }
  }

  const listedSubSteps = includeCalls
    ? [...[...subStepsByPhase.values()].flat(), ...otherSubSteps]
    : [];
  if (hasTimeline) {
    markParallel(listedSubSteps);
  }

  const byPhase = TURN_PHASES.flatMap((phase): PhaseTiming[] => {
    const phaseStages = stages.filter((stage) => stage.phase === phase);
    const workMs = phaseStages.reduce((sum, stage) => sum + stage.ms, 0);
    const subSteps = (subStepsByPhase.get(phase) ?? []).sort(byOrder);
    if (workMs <= 0 && subSteps.length === 0) {
      return [];
    }
    const spans = spansByPhase.get(phase) ?? [];
    const parallelStages = hasTimeline
      ? phaseStages
          .filter((stage) =>
            stages.some((other) => other !== stage && ranAlongside(stage.span, other.span)),
          )
          .map((stage) => stage.label)
      : [];
    return [
      {
        phase,
        ms: hasTimeline ? coveredLength(spans) : workMs,
        workMs,
        spans,
        parallelStages: [...new Set(parallelStages)],
        subSteps,
      },
    ];
  });

  const allPhaseSpans = [...spansByPhase.values()].flat();
  const otherSpans = hasTimeline && totalMs !== null ? gapsWithin(totalMs, allPhaseSpans) : [];
  const measuredMs = hasTimeline
    ? coveredLength(allPhaseSpans)
    : byPhase.reduce((sum, entry) => sum + entry.ms, 0);
  const otherMs = totalMs === null ? 0 : Math.max(0, totalMs - measuredMs);

  // The steps listed at the finest level shown: the calls when they are listed, else the stages.
  const detail: Array<{ ms: number; span: TimeSpan | null }> =
    listedSubSteps.length > 0 ? listedSubSteps : stages;
  const workMs = detail.reduce((sum, entry) => sum + entry.ms, 0);
  const coveredMs = hasTimeline
    ? coveredLength(detail.flatMap((entry) => (entry.span ? [entry.span] : [])))
    : workMs;

  return {
    totalMs,
    byPhase,
    otherMs,
    otherSpans,
    otherSubSteps: includeCalls ? otherSubSteps.sort(byOrder) : [],
    hasTimeline,
    workMs,
    coveredMs,
    parallelSavedMs: Math.max(0, workMs - coveredMs),
  };
}

/** Sub-steps are often under a second, where `formatElapsed` would floor them all to `0s`. */
export function formatStepDuration(ms: number): string {
  if (ms < 1000) {
    return `${Math.max(0, Math.round(ms))} ms`;
  }
  if (ms < 60_000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return formatElapsed(ms / 1000);
}
